<!-- 客户价值分层 -->
<template>
  <div class="customer-value-page">
    <section class="panel search-panel">
      <el-form :inline="true" size="small" :model="query">
        <el-form-item label="客户">
        <el-select
          v-model="query.custNo"
          clearable
          filterable
          placeholder="请选择客户"
          :loading="customerLoading"
          class="w-220"
        >
            <el-option
              v-for="item in customerOptions"
              :key="item.custNo || item.id"
              :label="item.custName || item.deptName"
              :value="String(item.custNo || item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="统计周期">
          <el-date-picker
            v-model="query.year"
            type="year"
            placeholder="选择统计年份"
            value-format="yyyy"
            class="w-260"
          />
        </el-form-item>
        <el-form-item label="客户分层">
          <el-select v-model="query.valueLevel" clearable placeholder="全部分层" class="w-140">
            <el-option v-for="item in levelOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="合同履约率">
          <el-select v-model="query.fulfillmentRange" clearable placeholder="全部区间" class="w-140">
            <el-option v-for="item in fulfillmentOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="逾期情况">
          <el-select v-model="query.hasOverdue" clearable placeholder="全部" class="w-110">
            <el-option label="有逾期" :value="true" />
            <el-option label="无逾期" :value="false" />
          </el-select>
        </el-form-item>
        <el-form-item label="异常车辆">
          <el-select v-model="query.hasAbnormalVehicle" clearable placeholder="全部" class="w-110">
            <el-option label="有异常" :value="true" />
            <el-option label="无异常" :value="false" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </section>

    <section class="print-action-panel">
      <el-button size="small" type="success" icon="el-icon-printer" @click="handleExportPdf">导出PDF</el-button>
    </section>

    <section class="metric-grid">
      <div class="metric score-card">
        <div class="score-ring" :style="{ background: scoreRingBg }">
          <span>{{ summary.avgScore || 0 }}</span>
        </div>
        <div>
          <div class="metric-label">整体客户价值指数</div>
          <div class="metric-value">{{ scoreLevelText }}</div>
          <div class="metric-extra">高价值客户 {{ summary.highValueCount || 0 }} 家，风险客户 {{ summary.riskCount || 0 }} 家</div>
        </div>
      </div>
      <div class="metric">
        <div class="metric-label">客户总数</div>
        <div class="metric-value">{{ number(summary.customerTotal) }}</div>
        <div class="metric-extra">当前筛选周期客户数量</div>
      </div>
      <div class="metric">
        <div class="metric-label">高价值客户</div>
        <div class="metric-value green">{{ number(summary.highValueCount) }}</div>
        <div class="metric-extra">建议优先保障计划资源</div>
      </div>
      <div class="metric">
        <div class="metric-label">成长型客户</div>
        <div class="metric-value blue">{{ number(summary.growthCount) }}</div>
        <div class="metric-extra">关注发运频次提升</div>
      </div>
      <div class="metric">
        <div class="metric-label">风险型客户</div>
        <div class="metric-value red">{{ number(summary.riskCount) }}</div>
        <div class="metric-extra">关注逾期和异常车辆</div>
      </div>
      <div class="metric">
        <div class="metric-label">平均合同履约率</div>
        <div class="metric-value purple">{{ percent(summary.avgContractFulfillmentRate) }}</div>
        <div class="metric-extra">按客户综合履约率均值</div>
      </div>
    </section>

    <section class="main-grid">
      <div class="panel">
        <div class="section-head">
          <div>
            <h3>客户分层分布</h3>
            <p>按当前统计周期综合评分自动归类</p>
          </div>
        </div>
        <div class="level-list">
          <div v-for="item in levelCards" :key="item.value" class="level-card">
            <div class="level-top">
              <span :class="item.color">{{ item.label }}</span>
              <b>{{ number(item.count) }}</b>
            </div>
            <div class="bar"><span :class="item.color" :style="{ width: item.rate + '%' }"></span></div>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>

      <div class="panel">
        <div class="section-head">
          <div>
            <h3>价值评分矩阵</h3>
            <p>横轴为结算贡献，纵轴为合同履约率，气泡大小代表订单规模</p>
          </div>
        </div>
        <div class="matrix-wrap">
          <span class="axis-y">合同履约率</span>
          <span class="axis-x">结算贡献</span>
          <span class="quadrant q1">高贡献高履约</span>
          <span class="quadrant q2">低贡献高履约</span>
          <span class="quadrant q3">低贡献低履约</span>
          <span class="quadrant q4">高贡献低履约</span>
          <span
            v-for="item in matrixBubbles"
            :key="item.custNo"
            class="bubble"
            :class="levelMeta(item.valueLevel).color"
            :style="bubbleStyle(item)"
            :title="item.custName"
          >{{ shortName(item.custName) }}</span>
        </div>
      </div>

      <div class="panel">
        <div class="section-head">
          <div>
            <h3>运营策略</h3>
            <p>按分层给出默认经营动作</p>
          </div>
        </div>
        <div class="strategy-list">
          <div v-for="item in strategyList" :key="item.title" class="strategy-card">
            <div><b>{{ item.title }}</b><el-tag size="mini" :type="item.type">{{ item.tag }}</el-tag></div>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="sub-grid">
      <div class="panel">
        <div class="section-head">
          <div>
            <h3>评分规则</h3>
            <p>权重用于页面展示，具体口径由后端聚合接口统一输出</p>
          </div>
        </div>
        <div class="rule-grid">
          <div v-for="item in ruleList" :key="item.name" class="rule-card">
            <div>{{ item.name }}</div>
            <b>{{ item.weight }}</b>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
      <div class="panel">
        <div class="section-head">
          <div>
            <h3>分层趋势</h3>
            <p>展示最近周期变化趋势，接口未返回趋势时使用当前分布预览</p>
          </div>
        </div>
        <div class="trend-wrap">
          <div v-for="item in levelCards" :key="item.value" class="trend-row">
            <span>{{ item.label }}</span>
            <div class="trend-track"><i :class="item.color" :style="{ width: item.rate + '%' }"></i></div>
            <b>{{ item.rate }}%</b>
          </div>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="table-head">
        <div>
          <h3>客户分层明细</h3>
          <p>结算贡献 = 合同结算项金额合计 + 支付成功的司机支付金额合计</p>
        </div>
      </div>
      <el-table v-loading="loading" :data="tableData" border size="small" empty-text="暂无客户分层数据">
        <el-table-column type="index" label="序号" width="56" align="center" />
        <el-table-column prop="custName" label="客户名称" min-width="190" show-overflow-tooltip />
        <el-table-column label="客户分层" width="100">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="levelMeta(row.valueLevel).type">{{ levelMeta(row.valueLevel).label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="orderScale" label="订单规模(吨)" width="120" align="right" :formatter="amountFormatter" />
        <el-table-column prop="shipFrequency" label="发运频次" width="100" align="right" />
        <el-table-column label="结算贡献(万元)" width="130" align="right">
          <template slot-scope="{ row }">{{ wan(row.settlementContributionAmount) }}</template>
        </el-table-column>
        <el-table-column prop="overdueCount" label="逾期次数" width="90" align="right" />
        <el-table-column prop="abnormalVehicleCount" label="异常车辆" width="100" align="right" />
        <el-table-column label="合同履约率" width="110" align="right">
          <template slot-scope="{ row }">{{ percent(row.contractFulfillmentRate) }}</template>
        </el-table-column>
        <el-table-column label="综合评分" width="95">
          <template slot-scope="{ row }"><span class="score-pill">{{ number(row.score) }}</span></template>
        </el-table-column>
        <el-table-column prop="suggestion" label="建议动作" width="120" show-overflow-tooltip />
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="goPortrait(row)">画像</el-button>
            <el-button type="text" size="small" @click="goBehavior(row)">运输行为</el-button>
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination
          background
          :current-page.sync="page.currentPage"
          :page-size.sync="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="page.total"
          @size-change="loadPage"
          @current-change="loadPage"
        />
      </div>
    </section>

    <el-drawer
      :visible.sync="detailVisible"
      size="82%"
      direction="rtl"
      append-to-body
      custom-class="customer-value-drawer"
      @closed="detail = null"
    >
      <template slot="title">
        <div>
          <b>{{ detailTitle }}</b>
          <p>{{ levelMeta(detailBase.valueLevel).label }} / 综合评分 {{ number(detailBase.score) }}</p>
        </div>
      </template>
      <div v-loading="detailLoading" class="drawer-body">
        <div class="drawer-section">
          <h3>核心指标</h3>
          <div class="drawer-metrics">
            <div v-for="item in detailMetricList" :key="item.label" class="info-item">
              <span>{{ item.label }}</span>
              <b>{{ item.value }}</b>
            </div>
          </div>
          <div class="source-note">
            口径说明：结算贡献 = 统计周期内客户合同结算项金额合计 + 支付成功的司机支付金额合计，仅用于客户价值分层，不等同于财务收入确认。
          </div>
        </div>

        <div class="drawer-section">
          <h3>合同结算项明细</h3>
          <el-table :data="detailContractItems" border size="mini" empty-text="暂无合同结算项明细">
            <el-table-column prop="settlementNo" label="结算单号" width="140" show-overflow-tooltip />
            <el-table-column prop="contractNo" label="合同编号" width="140" show-overflow-tooltip />
            <el-table-column prop="settlementItemName" label="结算项" min-width="120" show-overflow-tooltip />
            <el-table-column prop="cargoName" label="煤种" width="100" show-overflow-tooltip />
            <el-table-column prop="settlementWeight" label="结算量" width="100" align="right" :formatter="amountFormatter" />
            <el-table-column label="金额(万元)" width="120" align="right">
              <template slot-scope="{ row }">{{ wan(row.amount) }}</template>
            </el-table-column>
            <el-table-column prop="settlementDate" label="结算日期" width="120" />
          </el-table>
        </div>

        <div class="drawer-section">
          <h3>司机支付归集明细</h3>
          <el-table :data="detailDriverPayments" border size="mini" empty-text="暂无司机支付归集明细">
            <el-table-column prop="paymentOrderId" label="支付单号" width="150" show-overflow-tooltip />
            <el-table-column prop="bizNo" label="派车单号" width="140" show-overflow-tooltip />
            <el-table-column prop="driverName" label="司机" width="100" show-overflow-tooltip />
            <el-table-column prop="vehicleNo" label="车牌号" width="110" show-overflow-tooltip />
            <el-table-column prop="varName" label="煤种" width="100" show-overflow-tooltip />
            <el-table-column label="金额(万元)" width="120" align="right">
              <template slot-scope="{ row }">{{ wan(row.paymentAmount) }}</template>
            </el-table-column>
            <el-table-column label="支付状态" width="100">
              <template slot-scope="{ row }"><el-tag size="mini" type="success">{{ paymentStatusLabel(row.paymentStatus) }}</el-tag></template>
            </el-table-column>
            <el-table-column prop="paymentTime" label="支付时间" width="160" />
          </el-table>
        </div>

        <div class="drawer-section drawer-two-col">
          <div>
            <h3>评分拆解</h3>
            <div v-for="item in detailScoreBreakdown" :key="item.name" class="factor-row">
              <span>{{ item.name }}</span>
              <el-progress :percentage="Number(item.score || 0)" :show-text="false" />
              <b>{{ number(item.score) }}</b>
            </div>
          </div>
          <div>
            <h3>运营建议</h3>
            <ol class="advice-list">
              <li v-for="item in detailSuggestions" :key="item">{{ item }}</li>
            </ol>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  getCustomerOptions,
  getCustomerValueDetail,
  getCustomerValuePage,
  getCustomerValueSummary,
  getLegacyList,
} from './api';

const LEVEL_META = {
  HIGH: { label: '高价值', type: 'success', color: 'green' },
  GROWTH: { label: '成长型', type: '', color: 'blue' },
  STABLE: { label: '稳定型', type: 'info', color: 'slate' },
  RISK: { label: '风险型', type: 'danger', color: 'red' },
};

export default {
  name: 'automobileCustomerStratification',
  data() {
    return {
      loading: false,
      detailLoading: false,
      customerLoading: false,
      detailVisible: false,
      customerOptions: [],
      query: this.defaultQuery(),
      page: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
      },
      summary: {},
      tableData: [],
      detail: null,
      levelOptions: [
        { label: '高价值', value: 'HIGH' },
        { label: '成长型', value: 'GROWTH' },
        { label: '稳定型', value: 'STABLE' },
        { label: '风险型', value: 'RISK' },
      ],
      fulfillmentOptions: [
        { label: '90%以上', value: '90-' },
        { label: '80% - 90%', value: '80-90' },
        { label: '80%以下', value: '-80' },
      ],
      ruleList: [
        { name: '订单规模', weight: '25%', desc: '统计周期内订单总量和合同规模。' },
        { name: '发运频次', weight: '15%', desc: '车辆出库频率及连续发运稳定性。' },
        { name: '结算贡献', weight: '25%', desc: '合同结算项与司机支付归集金额。' },
        { name: '逾期情况', weight: '10%', desc: '逾期次数、金额和持续天数。' },
        { name: '异常车辆', weight: '10%', desc: '异常车辆预警、滞留及违规记录。' },
        { name: '合同履约率', weight: '15%', desc: '合同计划量与实际完成量比例。' },
      ],
      strategyList: [
        { title: '高价值客户', tag: '优先保障', type: 'success', desc: '纳入重点客户池，优先保障出库计划与装车资源。' },
        { title: '成长型客户', tag: '提频培育', type: '', desc: '关注发运频次和履约变化，推动转化为高价值客户。' },
        { title: '稳定型客户', tag: '常规维护', type: 'info', desc: '维持稳定服务，周期性回访合同执行和煤种偏好。' },
        { title: '风险型客户', tag: '联动预警', type: 'danger', desc: '跟踪逾期、异常车辆和履约缺口，必要时进行风险管控。' },
      ],
    };
  },
  computed: {
    scoreRingBg() {
      const score = Number(this.summary.avgScore || 0);
      return `conic-gradient(#13a86b 0 ${score * 3.6}deg, #edf1f6 ${score * 3.6}deg 360deg)`;
    },
    scoreLevelText() {
      const score = Number(this.summary.avgScore || 0);
      if (score >= 85) return '优秀';
      if (score >= 70) return '良好';
      if (score >= 55) return '稳定';
      return '待提升';
    },
    levelCards() {
      const total = Number(this.summary.customerTotal || 0) || 1;
      return [
        { value: 'HIGH', count: Number(this.summary.highValueCount || 0), desc: '贡献高、履约稳定，建议优先保障。' },
        { value: 'GROWTH', count: Number(this.summary.growthCount || 0), desc: '增长潜力明显，建议提频培育。' },
        { value: 'STABLE', count: Number(this.summary.stableCount || 0), desc: '表现稳定，适合常规维护。' },
        { value: 'RISK', count: Number(this.summary.riskCount || 0), desc: '存在风险信号，建议联动预警。' },
      ].map((item) => {
        const meta = this.levelMeta(item.value);
        return { ...item, ...meta, rate: Math.round(item.count / total * 100) };
      });
    },
    matrixBubbles() {
      return this.tableData.slice(0, 16);
    },
    detailBase() {
      return (this.detail && this.detail.baseInfo) || {};
    },
    detailMetrics() {
      return (this.detail && this.detail.metrics) || {};
    },
    detailTitle() {
      return this.detailBase.custName || this.detailMetrics.custName || '客户详情';
    },
    detailContractItems() {
      return (this.detail && this.detail.contractSettlementItems) || [];
    },
    detailDriverPayments() {
      return (this.detail && this.detail.driverPaymentItems) || [];
    },
    detailScoreBreakdown() {
      return (this.detail && this.detail.scoreBreakdown) || [];
    },
    detailSuggestions() {
      return (this.detail && this.detail.suggestions) || [];
    },
    detailMetricList() {
      const m = this.detailMetrics;
      return [
        { label: '订单规模', value: `${this.amount(m.orderScale)} 吨` },
        { label: '发运频次', value: `${this.number(m.shipFrequency)} 次` },
        { label: '结算贡献', value: `${this.wan(m.settlementContributionAmount)} 万元` },
        { label: '合同结算项', value: `${this.wan(m.contractSettlementAmount)} 万元` },
        { label: '司机支付归集', value: `${this.wan(m.driverPaymentAmount)} 万元` },
        { label: '合同履约率', value: this.percent(m.contractFulfillmentRate) },
      ];
    },
  },
  created() {
    this.loadCustomerOptions();
    this.loadData();
  },
  methods: {
    defaultQuery() {
      const now = new Date();
      return {
        custNo: '',
        year: String(now.getFullYear()),
        valueLevel: '',
        fulfillmentRange: '',
        hasOverdue: '',
        hasAbnormalVehicle: '',
      };
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    buildParams() {
      const params = {
        page: this.page.currentPage,
        size: this.page.pageSize,
        custNo: this.query.custNo,
        valueLevel: this.query.valueLevel,
        hasOverdue: this.query.hasOverdue === '' ? null : this.query.hasOverdue,
        hasAbnormalVehicle: this.query.hasAbnormalVehicle === '' ? null : this.query.hasAbnormalVehicle,
      };
      if (this.query.year) {
        params.startTime = `${this.query.year}-01-01 00:00:00`;
        params.endTime = `${this.query.year}-12-31 23:59:59`;
      }
      if (this.query.fulfillmentRange) {
        const parts = this.query.fulfillmentRange.split('-');
        params.contractFulfillmentMin = parts[0] ? Number(parts[0]) : null;
        params.contractFulfillmentMax = parts[1] ? Number(parts[1]) : null;
      }
      return params;
    },
    async loadData() {
      this.loading = true;
      const params = this.buildParams();
      try {
        const [summaryRes, pageRes] = await Promise.all([
          getCustomerValueSummary(params),
          getCustomerValuePage(params),
        ]);
        const pageData = pageRes.data && pageRes.data.data ? pageRes.data.data : {};
        this.tableData = (pageData.records || []).map(this.normalizeRow);
        this.page.total = Number(pageData.total || 0);
        this.summary = summaryRes.data && summaryRes.data.data ? summaryRes.data.data : this.buildSummary(this.tableData);
      } catch (e) {
        await this.loadLegacyData(params);
      } finally {
        this.loading = false;
      }
    },
    async loadLegacyData(params) {
      const res = await getLegacyList(params);
      const data = res.data && res.data.data ? res.data.data : {};
      this.tableData = (data.records || []).map(this.normalizeRow);
      this.page.total = Number(data.total || this.tableData.length);
      this.summary = this.buildSummary(this.tableData);
    },
    loadPage() {
      this.loadData();
    },
    handleSearch() {
      this.page.currentPage = 1;
      this.loadData();
    },
    handleReset() {
      this.query = this.defaultQuery();
      this.page.currentPage = 1;
      this.loadData();
    },
    async loadCustomerOptions() {
      this.customerLoading = true;
      try {
        const res = await getCustomerOptions({});
        const list = res.data && res.data.data ? res.data.data : res.data;
        this.customerOptions = Array.isArray(list) ? list : [];
      } finally {
        this.customerLoading = false;
      }
    },
    async openDetail(row) {
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const params = this.buildParams();
        const res = await getCustomerValueDetail({
          custNo: row.custNo,
          startTime: params.startTime,
          endTime: params.endTime,
        });
        this.detail = res.data && res.data.data ? res.data.data : this.localDetail(row);
      } catch (e) {
        this.detail = this.localDetail(row);
      } finally {
        this.detailLoading = false;
      }
    },
    localDetail(row) {
      return {
        baseInfo: row,
        metrics: row,
        scoreBreakdown: this.ruleList.map((item) => ({ name: item.name, score: row.score || 0 })),
        contractSettlementItems: [],
        driverPaymentItems: [],
        suggestions: [row.suggestion || '按当前分层策略持续跟踪客户经营表现。'],
      };
    },
    buildSummary(records) {
      const summary = {
        customerTotal: this.page.total || records.length,
        highValueCount: 0,
        growthCount: 0,
        stableCount: 0,
        riskCount: 0,
        settlementContributionAmount: 0,
        contractSettlementAmount: 0,
        driverPaymentAmount: 0,
        avgContractFulfillmentRate: 0,
        avgScore: 0,
      };
      records.forEach((row) => {
        const level = this.normalizeLevel(row.valueLevel);
        if (level === 'HIGH') summary.highValueCount += 1;
        if (level === 'GROWTH') summary.growthCount += 1;
        if (level === 'STABLE') summary.stableCount += 1;
        if (level === 'RISK') summary.riskCount += 1;
        summary.settlementContributionAmount += this.toNumber(row.settlementContributionAmount);
        summary.contractSettlementAmount += this.toNumber(row.contractSettlementAmount);
        summary.driverPaymentAmount += this.toNumber(row.driverPaymentAmount);
        summary.avgContractFulfillmentRate += this.toNumber(row.contractFulfillmentRate);
        summary.avgScore += this.toNumber(row.score);
      });
      const len = records.length || 1;
      summary.avgContractFulfillmentRate = Number((summary.avgContractFulfillmentRate / len).toFixed(2));
      summary.avgScore = Math.round(summary.avgScore / len);
      return summary;
    },
    normalizeRow(row) {
      const valueLevel = this.normalizeLevel(row.valueLevel);
      const settlement = row.settlementContributionAmount != null ? row.settlementContributionAmount : row.profitContribution;
      const score = row.score != null ? row.score : this.estimateScore(row, valueLevel);
      return {
        ...row,
        valueLevel,
        settlementContributionAmount: this.toNumber(settlement),
        contractSettlementAmount: this.toNumber(row.contractSettlementAmount || settlement),
        driverPaymentAmount: this.toNumber(row.driverPaymentAmount),
        overdueCount: this.toNumber(row.overdueCount),
        abnormalVehicleCount: this.toNumber(row.abnormalVehicleCount),
        contractFulfillmentRate: this.toNumber(row.contractFulfillmentRate || row.executeProgress),
        score,
        suggestion: row.suggestion || this.levelSuggestion(valueLevel),
      };
    },
    normalizeLevel(level) {
      const map = { 2: 'HIGH', 1: 'GROWTH', 0: 'STABLE', HIGH: 'HIGH', GROWTH: 'GROWTH', STABLE: 'STABLE', RISK: 'RISK' };
      return map[level] || 'STABLE';
    },
    estimateScore(row, level) {
      if (level === 'HIGH') return 90;
      if (level === 'GROWTH') return 76;
      if (level === 'RISK') return 48;
      return row.contractFulfillmentRate ? Math.round(row.contractFulfillmentRate) : 66;
    },
    levelSuggestion(level) {
      const map = { HIGH: '优先保障', GROWTH: '提频培育', STABLE: '常规维护', RISK: '联动预警' };
      return map[level] || '常规维护';
    },
    levelMeta(level) {
      return LEVEL_META[this.normalizeLevel(level)] || LEVEL_META.STABLE;
    },
    bubbleStyle(row) {
      const maxSettle = Math.max(...this.tableData.map((i) => this.toNumber(i.settlementContributionAmount)), 1);
      const maxScale = Math.max(...this.tableData.map((i) => this.toNumber(i.orderScale)), 1);
      const left = 12 + Math.min(78, this.toNumber(row.settlementContributionAmount) / maxSettle * 78);
      const top = 78 - Math.min(68, this.toNumber(row.contractFulfillmentRate) / 100 * 68);
      const size = 34 + Math.min(28, this.toNumber(row.orderScale) / maxScale * 28);
      return { left: `${left}%`, top: `${top}%`, width: `${size}px`, height: `${size}px` };
    },
    shortName(name) {
      return name ? String(name).replace(/有限公司|集团|贸易|能源/g, '').slice(0, 2) : '--';
    },
    goPortrait(row) {
      this.$router.push({ path: '/automobile-management/customer-transport-portrait/index', query: { custNo: row.custNo } });
    },
    goBehavior(row) {
      this.$router.push({ path: '/automobile-management/transportation-behavior/index', query: { custNo: row.custNo } });
    },
    handleExportPdf() {
      document.body.classList.add('customer-value-printing');
      const clearPrintClass = () => {
        document.body.classList.remove('customer-value-printing');
        window.removeEventListener('afterprint', clearPrintClass);
      };
      window.addEventListener('afterprint', clearPrintClass);
      this.$nextTick(() => {
        window.print();
        setTimeout(clearPrintClass, 1000);
      });
    },
    amountFormatter(row, column, value) {
      return this.amount(value);
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0;
      }
      const n = Number(String(value).replace(/,/g, ''));
      return Number.isFinite(n) ? n : 0;
    },
    amount(value) {
      const n = this.toNumber(value);
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
    },
    number(value) {
      const n = this.toNumber(value);
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
    },
    wan(value) {
      const n = this.toNumber(value) / 10000;
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    },
    percent(value) {
      const n = this.toNumber(value);
      return `${n.toLocaleString('zh-CN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    },
    paymentStatusLabel(value) {
      const map = { 1: '未付款', 2: '已付款', 3: '付款失败', 4: '已取消' };
      return map[value] || value || '-';
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-value-page {
  min-width: 1280px;
  padding: 12px;
  color: #1f2937;
  background: #FFFFFF;

  .section-head,
  .table-head,
  .level-top,
  .strategy-card > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  p,
  .metric-label,
  .metric-extra {
    margin: 0;
    color: #667085;
    font-size: 13px;
  }

  h3 {
    margin: 0;
    color: #111827;
    font-weight: 600;
  }

  h3 {
    font-size: 16px;
  }

  .panel,
  .metric,
  .level-card,
  .strategy-card,
  .rule-card {
    border: 1px solid #dde5ef;
    border-radius: 6px;
    background: #fff;
  }

  .panel {
    margin-bottom: 12px;
    padding: 14px 16px;
  }

  .search-panel {
    padding-bottom: 2px;
  }

  .print-action-panel {
    display: flex;
    justify-content: flex-end;
    margin: -4px 0 12px;
  }

  .w-110 { width: 110px; }
  .w-140 { width: 140px; }
  .w-220 { width: 220px; }
  .w-260 { width: 260px; }

  .metric-grid {
    display: grid;
    grid-template-columns: 1.25fr repeat(5, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }

  .metric {
    min-height: 96px;
    padding: 12px;
  }

  .score-card {
    display: grid;
    grid-template-columns: 86px 1fr;
    gap: 12px;
    align-items: center;
  }

  .score-ring {
    position: relative;
    width: 86px;
    height: 86px;
    border-radius: 50%;

    &::after {
      position: absolute;
      inset: 10px;
      border-radius: 50%;
      background: #fff;
      content: "";
    }

    span {
      position: absolute;
      inset: 0;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #0b8f5a;
      font-size: 24px;
      font-weight: 700;
    }
  }

  .metric-value {
    margin-top: 7px;
    color: #111827;
    font-size: 23px;
    font-weight: 600;
  }

  .metric-extra {
    margin-top: 5px;
    font-size: 12px;
  }

  .green { color: #0b8f5a; }
  .blue { color: #1f7aec; }
  .slate { color: #6b7a90; }
  .red { color: #c8382b; }
  .purple { color: #7047b8; }

  .main-grid {
    display: grid;
    grid-template-columns: 1.02fr 1.18fr .9fr;
    gap: 12px;
  }

  .level-list,
  .strategy-list {
    display: grid;
    gap: 10px;
  }

  .level-card,
  .strategy-card,
  .rule-card {
    padding: 12px;
  }

  .level-top b {
    font-size: 20px;
  }

  .bar,
  .trend-track {
    height: 10px;
    overflow: hidden;
    border-radius: 10px;
    background: #edf1f6;

    span,
    i {
      display: block;
      height: 100%;
      border-radius: 10px;
    }
  }

  .bar .green,
  .trend-track .green { background: #13a86b; }
  .bar .blue,
  .trend-track .blue { background: #1f7aec; }
  .bar .slate,
  .trend-track .slate { background: #6b7a90; }
  .bar .red,
  .trend-track .red { background: #d92d20; }

  .level-card p,
  .strategy-card p,
  .rule-card p {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.6;
  }

  .matrix-wrap {
    position: relative;
    height: 366px;
    padding: 18px 18px 42px 52px;
    border: 1px solid #edf1f6;
    border-radius: 6px;
    background:
      linear-gradient(to top, #edf1f6 1px, transparent 1px) 0 0 / 100% 64px,
      linear-gradient(to right, #edf1f6 1px, transparent 1px) 0 0 / 86px 100%,
      #fff;
  }

  .axis-y,
  .axis-x,
  .quadrant {
    position: absolute;
    color: #667085;
    font-size: 12px;
  }

  .axis-y {
    left: 12px;
    top: 18px;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
  }

  .axis-x {
    right: 18px;
    bottom: 14px;
  }

  .q1 { right: 24px; top: 18px; }
  .q2 { left: 58px; top: 18px; }
  .q3 { left: 58px; bottom: 46px; }
  .q4 { right: 24px; bottom: 46px; }

  .bubble {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fff;
    border-radius: 50%;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 7px 18px rgba(31, 41, 55, .18);
    transform: translate(-50%, -50%);
  }

  .bubble.green { background: #13a86b; }
  .bubble.blue { background: #1f7aec; }
  .bubble.slate { background: #6b7a90; }
  .bubble.red { background: #d92d20; }

  .sub-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .rule-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
  }

  .rule-card b {
    display: block;
    margin-top: 8px;
    color: #1f7aec;
    font-size: 22px;
  }

  .trend-row {
    display: grid;
    grid-template-columns: 90px 1fr 48px;
    gap: 12px;
    align-items: center;
    margin-bottom: 18px;
  }

  .score-pill {
    display: inline-block;
    min-width: 46px;
    height: 24px;
    padding: 0 8px;
    border-radius: 12px;
    background: #eef7ff;
    color: #1f7aec;
    text-align: center;
    line-height: 24px;
    font-weight: 600;
  }

  .pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }
}

.drawer-body {
  padding: 0 20px 24px;
}

.drawer-section {
  margin-bottom: 18px;

  h3 {
    margin: 0 0 10px;
    color: #1f2937;
    font-size: 16px;
  }
}

.drawer-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.info-item {
  min-height: 62px;
  padding: 10px;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  background: #fafbfd;

  span {
    color: #667085;
    font-size: 12px;
  }

  b {
    display: block;
    margin-top: 6px;
    color: #111827;
    font-size: 15px;
  }
}

.source-note {
  margin-top: 8px;
  padding: 9px 10px;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: #f0f7ff;
  color: #475467;
  font-size: 12px;
  line-height: 1.6;
}

.drawer-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.factor-row {
  display: grid;
  grid-template-columns: 88px 1fr 38px;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  color: #475467;
  font-size: 13px;
}

.advice-list {
  margin: 0;
  padding-left: 20px;
  color: #475467;
  line-height: 1.9;
}

@media print {
  .customer-value-page {
    min-width: 0;
    padding: 0;
    background: #fff;
  }

  .search-panel,
  .print-action-panel,
  .pagination,
  ::v-deep .el-table__fixed-right {
    display: none;
  }
}
</style>

<style lang="scss">
@media print {
  body.customer-value-printing {
    background: #fff !important;
  }

  body.customer-value-printing .avue-header,
  body.customer-value-printing .avue-left,
  body.customer-value-printing .avue-tags,
  body.customer-value-printing .avue-shade,
  body.customer-value-printing .search-panel,
  body.customer-value-printing .print-action-panel,
  body.customer-value-printing .pagination,
  body.customer-value-printing .el-drawer__wrapper,
  body.customer-value-printing .v-modal {
    display: none !important;
  }

  body.customer-value-printing .avue-contail,
  body.customer-value-printing .avue-layout,
  body.customer-value-printing .avue-main,
  body.customer-value-printing .view-container,
  body.customer-value-printing .avue-view {
    position: static !important;
    width: auto !important;
    min-width: 0 !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: #fff !important;
  }

  body.customer-value-printing .customer-value-page {
    min-width: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  body.customer-value-printing .panel,
  body.customer-value-printing .metric,
  body.customer-value-printing .level-card,
  body.customer-value-printing .strategy-card,
  body.customer-value-printing .rule-card {
    break-inside: avoid;
  }
}
</style>
